import { useEffect } from 'react';
import $ from 'jquery';
import { useServiceResourcesQuery } from '../queries';
import { useBookingStore } from '../store/useBookingStore';
import {
	updateSelectOptions,
	updateRadioButtons,
} from '../utils/fieldRenderers';

/**
 * Hook to update resource field options when service changes.
 *
 * Reads all necessary data from the store, fetches new resources via React Query,
 * then updates both select dropdowns and radio button groups.
 */
export function useResourceFieldUpdater(): void {
	const formId = useBookingStore((state) => state.formId);
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	const serviceFieldId = useBookingStore((state) => state.serviceFieldId);
	const resourceFieldIds = useBookingStore((state) => state.resourceFieldIds);

	// Fetch resources using React Query
	const { data: resources } = useServiceResourcesQuery(
		{ id: currentServiceId },
		{
			enabled:
				currentServiceId > 0 &&
				serviceFieldId !== null &&
				resourceFieldIds.length > 0,
		}
	);

	// Update resource fields when resources are fetched
	useEffect(() => {
		if (!resources || resourceFieldIds.length === 0) {
			return;
		}

		// Update each resource field
		resourceFieldIds.forEach((fieldId) => {
			updateResourceField(formId, fieldId, resources);
		});

		// Note: Pricing updates are handled by useGravityFormsPricing
		// which watches resource field changes via useDOMFieldSync
	}, [resources, formId, resourceFieldIds]);
}

/**
 * Update a single resource field's options (select or radio).
 */
function updateResourceField(
	formId: number,
	fieldId: number,
	resources: Array<{ id: number; name: string }>
): void {
	const $field = $(`#field_${formId}_${fieldId}`);
	if (!$field.length) {
		return;
	}

	const $select = $field.find('select');
	const $radioContainer = $field.find('.gfield_radio');

	if ($select.length) {
		updateSelectOptions($select, resources, 'Select a resource');
	} else if ($radioContainer.length) {
		updateRadioButtons($radioContainer, resources, formId, fieldId);
	}
}
